/**
 * Pure reference-counting subscription manager for BookByPrice (BBP).
 *
 * Tracks which subscribers are interested in which symbols and fires
 * onFirstSubscriber / onLastUnsubscriber callbacks so the owner
 * (marketdata.jsx) can wire them to SignalR Register("bbp", ...) /
 * Unregister("bbp", ...) invocations.
 *
 * Zero external dependencies — fully unit-testable.
 *
 * Differences from QuoteSubscriptionManager:
 * - Requires a non-empty subscriberId on every register/unregister call.
 *   Missing/empty IDs are a no-op + warning instead of silently using a
 *   shared bucket. Convention: `book-${panelId}` (see Chart pattern).
 * - Exposes getActiveSymbols() for reconnect re-registration.
 * - getCanonicalSymbol defaults to identity. BBP intentionally does not
 *   reuse quote canonical (hardcoded WIN/WDO only) in Phase 2. The option
 *   is kept as a forward-compatible seam.
 */
export class BookByPriceSubscriptionManager {
  constructor(options = {}) {
    /** @type {Map<string, Map<string, string>>} canonical → Map<subscriberId, originalSymbol> */
    this.symbolSubscribers = new Map();

    this.onFirstSubscriber = options.onFirstSubscriber || (() => {});
    this.onLastUnsubscriber = options.onLastUnsubscriber || (() => {});
    this.getCanonicalSymbol = options.getCanonicalSymbol || ((s) => s);
  }

  _normalizeSymbol(symbol) {
    if (typeof symbol !== "string") return null;
    const trimmed = symbol.trim().toUpperCase();
    return trimmed.length > 0 ? trimmed : null;
  }

  _validateSubscriberId(subscriberId) {
    return typeof subscriberId === "string" && subscriberId.trim().length > 0;
  }

  register(symbol, subscriberId) {
    const normalized = this._normalizeSymbol(symbol);
    if (!normalized) {
      console.warn(
        "[BookByPriceSubscriptionManager] register: invalid symbol",
        symbol,
      );
      return;
    }
    if (!this._validateSubscriberId(subscriberId)) {
      console.warn(
        "[BookByPriceSubscriptionManager] register: subscriberId required",
        { symbol: normalized },
      );
      return;
    }

    const canonical = this.getCanonicalSymbol(normalized);

    if (!this.symbolSubscribers.has(canonical)) {
      this.symbolSubscribers.set(canonical, new Map());
    }

    const subscribers = this.symbolSubscribers.get(canonical);
    const isFirst = subscribers.size === 0;

    subscribers.set(subscriberId, normalized);

    if (isFirst) {
      this.onFirstSubscriber(canonical);
    }
  }

  unregister(symbol, subscriberId) {
    const normalized = this._normalizeSymbol(symbol);
    if (!normalized) return;
    if (!this._validateSubscriberId(subscriberId)) {
      console.warn(
        "[BookByPriceSubscriptionManager] unregister: subscriberId required",
        { symbol: normalized },
      );
      return;
    }

    const canonical = this.getCanonicalSymbol(normalized);
    if (!this.symbolSubscribers.has(canonical)) return;

    const subscribers = this.symbolSubscribers.get(canonical);
    if (!subscribers.has(subscriberId)) return;

    subscribers.delete(subscriberId);

    if (subscribers.size === 0) {
      this.symbolSubscribers.delete(canonical);
      this.onLastUnsubscriber(canonical);
    }
  }

  hasSubscribers(symbol) {
    const normalized = this._normalizeSymbol(symbol);
    if (!normalized) return false;
    const canonical = this.getCanonicalSymbol(normalized);
    const subs = this.symbolSubscribers.get(canonical);
    return subs != null && subs.size > 0;
  }

  /**
   * Snapshot of currently subscribed canonical symbols.
   * Used on reconnect to re-issue upstream Register("bbp", symbol) calls.
   * @returns {string[]}
   */
  getActiveSymbols() {
    return Array.from(this.symbolSubscribers.keys());
  }

  debug() {
    const data = [];
    this.symbolSubscribers.forEach((subscribers, symbol) => {
      data.push({
        symbol,
        refCount: subscribers.size,
        subscribers: Array.from(subscribers.keys()).join(", "),
      });
    });
    return data;
  }

  clear() {
    this.symbolSubscribers.clear();
  }
}
